
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { 
    FaPlus, FaEdit, FaTrash, FaSpinner, FaPlay, FaEye, FaTasks, FaCheck,
    FaSitemap, FaFileSignature, FaUser, FaClipboardCheck, FaChartPie
} from 'react-icons/fa';
import './AvaliacaoDesempenho.css'; // Importa o CSS novo
import { useAuth } from '../../hooks/useAuth'; // Para pegar o perfil
import { useNavigate } from 'react-router-dom'; // Para navegar para a tela de resposta

// Reutilizando os componentes de T&D
const Modal = ({ show, onClose, title, children, footer, size = 'default' }) => {
    if (!show) return null;
    const modalStyle = size === 'large' ? { maxWidth: '900px', width: '90%' } : { maxWidth: '700px', width: '90%' };
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={modalStyle}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button onClick={onClose} className="close-button">&times;</button>
                </div>
                <div className="modal-body">{children}</div>
                {footer && <div className="modal-footer">{footer}</div>}
            </div>
        </div>
    );
};

const LoadingSpinner = () => (
    <div className="loading-spinner">
        <FaSpinner className="fa-spin" size={30} />
        <span style={{ marginLeft: '10px' }}>Carregando...</span>
    </div>
);

// =================================================================
// === ABA 1: VISÃO GERAL (DASHBOARD DO USUÁRIO) ====================
// =================================================================
const VisaoGeralDashboard = ({ user }) => {
    const [pendencias, setPendencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPendencias = async () => {
            setLoading(true);
            try {
                const { data } = await api.get('/api/rh/desempenho/minhas-pendencias');
                setPendencias(data);
            } catch (error) {
                toast.error("Falha ao buscar avaliações pendentes.");
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchPendencias();
    }, []);

    const handleResponder = (avaliacaoId) => {
        // A tela de resposta não existe ainda, mas vamos criar a navegação
        // Vamos supor que ela estará em /rh/avaliacao/responder/:id
        // (Precisamos adicionar essa rota no App.js)
        toast.info("Em breve: Navegando para a tela de resposta...");
        // navigate(`/rh/avaliacao/responder/${avaliacaoId}`);
    };

    if (loading) return <LoadingSpinner />;

    return (
        <>
            <div className="ad-section">
                <div className="ad-section-header">
                    <h2>Minhas Avaliações Pendentes</h2>
                </div>
                <div className="pendencia-list">
                    {pendencias.filter(p => p.status !== 'concluida').length === 0 && (
                        <p>Você não possui avaliações pendentes. Bom trabalho!</p>
                    )}
                    {pendencias.filter(p => p.status !== 'concluida').map(p => (
                        <div key={p.id} className="pendencia-item">
                            <div className="pendencia-item-info">
                                <img src={p.foto_perfil || '/avatar-padrao.png'} alt="Foto" />
                                <div>
                                    <h4>
                                        {p.tipo_avaliacao === 'auto' ? "Minha Autoavaliação" : `Avaliar: ${p.avaliado_nome}`}
                                    </h4>
                                    <span>Ciclo: {p.ciclo_titulo}</span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <span className={`pendencia-status ${p.status}`}>
                                    {p.status === 'pendente' ? 'Pendente' : 'Em Andamento'}
                                </span>
                                <button 
                                    className="ad-button-primary" 
                                    onClick={() => handleResponder(p.id)}
                                >
                                    <FaFileSignature /> {p.status === 'pendente' ? 'Iniciar' : 'Continuar'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            
            <div className="ad-section">
                <div className="ad-section-header">
                    <h2>Meus Resultados Anteriores</h2>
                </div>
                {/* Aqui viria a lista de avaliações concluídas */}
                <p>Em breve: Visualize seus resultados consolidados dos ciclos anteriores.</p>
            </div>
        </>
    );
};

// =================================================================
// === ABA 2: MODELOS DE AVALIAÇÃO (ADMIN) =========================
// =================================================================
const GestaoModelos = ({ user, competencias }) => {
    const [modelos, setModelos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    
    const initialModelState = {
        titulo: '',
        descricao: '',
        status: 'rascunho',
        questoes: [] // Array de itens
    };
    const [novoModelo, setNovoModelo] = useState(initialModelState);
    const [currentQuestao, setCurrentQuestao] = useState({ tipo_pergunta: 'nota_1_5', competencia_id: '', texto_pergunta: '' });

    const fetchModelos = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/rh/desempenho/modelos');
            setModelos(data);
        } catch (error) { toast.error("Falha ao buscar modelos."); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchModelos(); }, []);

    // Handlers do Modal Principal
    const handleOpenModal = () => {
        setNovoModelo(initialModelState);
        setCurrentQuestao({ tipo_pergunta: 'nota_1_5', competencia_id: '', texto_pergunta: '' });
        setModalOpen(true);
    };
    const handleCloseModal = () => setModalOpen(false);

    const handleMasterChange = (e) => {
        const { name, value } = e.target;
        setNovoModelo(prev => ({ ...prev, [name]: value }));
    };

    // Handlers das Questões
    const handleQuestaoChange = (e) => {
        const { name, value } = e.target;
        setCurrentQuestao(prev => ({ ...prev, [name]: value }));
    };

    const handleAddQuestao = () => {
        if (currentQuestao.tipo_pergunta === 'nota_1_5' && !currentQuestao.competencia_id) {
            toast.warn("Selecione uma competência para perguntas de nota.");
            return;
        }
        if (currentQuestao.tipo_pergunta === 'texto_livre' && !currentQuestao.texto_pergunta) {
            toast.warn("Digite o texto da pergunta.");
            return;
        }

        const questaoComNome = {
            ...currentQuestao,
            temp_id: Date.now(),
            nome: currentQuestao.tipo_pergunta === 'nota_1_5'
                ? competencias.find(c => c.id === parseInt(currentQuestao.competencia_id))?.nome_competencia
                : currentQuestao.texto_pergunta
        };
        
        setNovoModelo(prev => ({ ...prev, questoes: [...prev.questoes, questaoComNome] }));
        setCurrentQuestao({ tipo_pergunta: 'nota_1_5', competencia_id: '', texto_pergunta: '' }); // Reseta
    };

    const handleRemoveQuestao = (temp_id) => {
        setNovoModelo(prev => ({ ...prev, questoes: prev.questoes.filter(q => q.temp_id !== temp_id) }));
    };

    // Submit do Modelo
    const handleSubmit = async () => {
        try {
            await api.post('/api/rh/desempenho/modelos', novoModelo);
            toast.success("Modelo criado com sucesso!");
            fetchModelos();
            handleCloseModal();
        } catch (error) {
            toast.error("Falha ao criar o modelo.");
            console.error(error);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="ad-section">
            <div className="ad-section-header">
                <h2>Modelos de Avaliação</h2>
                <button className="ad-button-primary" onClick={handleOpenModal}>
                    <FaPlus /> Novo Modelo
                </button>
            </div>
            
            <table className="ad-table">
                <thead><tr><th>Título</th><th>Nº de Questões</th><th>Status</th><th>Ações</th></tr></thead>
                <tbody>
                    {modelos.map(m => (
                        <tr key={m.id}>
                            <td>{m.titulo}</td>
                            <td>{m.questoes.length}</td>
                            <td>{m.status}</td>
                            <td><button className="edit" title="Ver/Editar"><FaEdit /></button></td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal show={modalOpen} onClose={handleCloseModal} title="Novo Modelo de Avaliação" size="large"
                footer={
                    <>
                        <button className="ad-button-secondary" onClick={handleCloseModal}>Cancelar</button>
                        <button className="ad-button-primary" onClick={handleSubmit}>Salvar Modelo</button>
                    </>
                }>
                
                <form className="ad-form">
                    <div className="ad-form-group" style={{ gridColumn: 'span 2' }}>
                        <label>Título do Modelo</label>
                        <input name="titulo" value={novoModelo.titulo} onChange={handleMasterChange} />
                    </div>
                    <div className="ad-form-group">
                        <label>Status</label>
                        <select name="status" value={novoModelo.status} onChange={handleMasterChange}>
                            <option value="rascunho">Rascunho</option>
                            <option value="ativo">Ativo</option>
                        </select>
                    </div>
                    <div className="ad-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Descrição</label>
                        <textarea name="descricao" value={novoModelo.descricao} onChange={handleMasterChange}></textarea>
                    </div>
                </form>
                
                <hr style={{ margin: '2rem 0' }} />
                
                <h3>Questões do Modelo</h3>
                <div className="ad-section" style={{ background: '#f9f9f9' }}>
                    <h4>Adicionar Nova Questão</h4>
                    <div className="ad-form">
                        <div className="ad-form-group">
                            <label>Tipo de Pergunta</label>
                            <select name="tipo_pergunta" value={currentQuestao.tipo_pergunta} onChange={handleQuestaoChange}>
                                <option value="nota_1_5">Nota (Baseada em Competência)</option>
                                <option value="texto_livre">Texto Livre (Aberta)</option>
                            </select>
                        </div>
                        
                        {currentQuestao.tipo_pergunta === 'nota_1_5' ? (
                            <div className="ad-form-group" style={{ gridColumn: 'span 2' }}>
                                <label>Competência (do T&D)</label>
                                <select name="competencia_id" value={currentQuestao.competencia_id} onChange={handleQuestaoChange}>
                                    <option value="">-- Selecione a competência --</option>
                                    {competencias.map(c => <option key={c.id} value={c.id}>{c.nome_competencia} ({c.tipo})</option>)}
                                </select>
                            </div>
                        ) : (
                            <div className="ad-form-group" style={{ gridColumn: 'span 2' }}>
                                <label>Texto da Pergunta</label>
                                <input name="texto_pergunta" value={currentQuestao.texto_pergunta} onChange={handleQuestaoChange} />
                            </div>
                        )}
                        <div className="ad-form-group" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="ad-button-primary" onClick={handleAddQuestao}>
                                Adicionar Questão
                            </button>
                        </div>
                    </div>
                </div>

                <h4>Questões Adicionadas</h4>
                {novoModelo.questoes.map(q => (
                    <div key={q.temp_id} className="questao-item">
                        <div className="questao-header">
                            <h5>{q.nome}</h5>
                            <button className="remove-item-btn" onClick={() => handleRemoveQuestao(q.temp_id)}><FaTrash /></button>
                        </div>
                        <span>Tipo: {q.tipo_pergunta === 'nota_1_5' ? 'Nota 1-5' : 'Texto Livre'}</span>
                    </div>
                ))}
                {novoModelo.questoes.length === 0 && <p>Nenhuma questão adicionada.</p>}
            </Modal>
        </div>
    );
};

// =================================================================
// === ABA 3: CICLOS DE AVALIAÇÃO (ADMIN) ==========================
// =================================================================
const GestaoCiclos = ({ user, modelos }) => {
    const [ciclos, setCiclos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    
    const initialCicloState = {
        titulo: `Ciclo ${new Date().getFullYear()}`,
        data_inicio: '',
        data_fim_auto: '',
        data_fim_gestor: '',
        modelo_id_lider: '',
        modelo_id_liderado: ''
    };
    const [novoCiclo, setNovoCiclo] = useState(initialCicloState);

    const fetchCiclos = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/rh/desempenho/ciclos');
            setCiclos(data);
        } catch (error) { toast.error("Falha ao buscar ciclos."); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchCiclos(); }, []);

    const handleOpenModal = () => {
        setNovoCiclo(initialCicloState);
        setModalOpen(true);
    };
    const handleCloseModal = () => setModalOpen(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNovoCiclo(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            await api.post('/api/rh/desempenho/ciclos', novoCiclo);
            toast.success("Ciclo criado como rascunho! Agora você pode iniciá-lo.");
            fetchCiclos();
            handleCloseModal();
        } catch (error) {
            toast.error("Falha ao criar o ciclo.");
            console.error(error);
        }
    };
    
    const handleIniciarCiclo = async (cicloId) => {
        if (!window.confirm("Atenção: Esta ação é irreversível. Deseja disparar este ciclo para todos os colaboradores elegíveis?")) {
            return;
        }
        try {
            const { data } = await api.post(`/api/rh/desempenho/ciclos/${cicloId}/iniciar`);
            toast.success(data.message);
            fetchCiclos(); // Atualiza o status na tabela
        } catch (error) {
            toast.error(error.response?.data?.error || "Falha ao iniciar o ciclo.");
            console.error(error);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="ad-section">
            <div className="ad-section-header">
                <h2>Ciclos de Avaliação</h2>
                <button className="ad-button-primary" onClick={handleOpenModal}>
                    <FaPlus /> Novo Ciclo
                </button>
            </div>
            
            <table className="ad-table">
                <thead>
                    <tr>
                        <th>Título</th>
                        <th>Status</th>
                        <th>Modelo (Liderados)</th>
                        <th>Modelo (Líderes)</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {ciclos.map(c => (
                        <tr key={c.id}>
                            <td>{c.titulo}</td>
                            <td>{c.status}</td>
                            <td>{c.modelo_liderado_nome || 'N/A'}</td>
                            <td>{c.modelo_lider_nome || '(usa modelo liderado)'}</td>
                            <td>
                                {c.status === 'rascunho' && (
                                    <button 
                                        className="ad-button-primary" 
                                        onClick={() => handleIniciarCiclo(c.id)}
                                        title="Disparar ciclo"
                                        style={{ backgroundColor: '#2E7D32' /* Verde */ }}
                                    >
                                        <FaPlay /> Iniciar
                                    </button>
                                )}
                                {c.status === 'ativo' && (
                                    <button className="ad-button-secondary" title="Acompanhar">
                                        <FaChartPie /> Acompanhar
                                    </button>
                                )}
                                 {c.status === 'concluido' && (
                                    <button className="ad-button-secondary" title="Ver Resultados">
                                        <FaEye /> Resultados
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal show={modalOpen} onClose={handleCloseModal} title="Novo Ciclo de Avaliação" size="large">
                <form className="ad-form">
                    <div className="ad-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Título do Ciclo (Ex: Ciclo 2025/S1)</label>
                        <input name="titulo" value={novoCiclo.titulo} onChange={handleChange} />
                    </div>
                    <div className="ad-form-group">
                        <label>Data de Início</label>
                        <input type="date" name="data_inicio" value={novoCiclo.data_inicio} onChange={handleChange} />
                    </div>
                    <div className="ad-form-group">
                        <label>Prazo Autoavaliação</label>
                        <input type="date" name="data_fim_auto" value={novoCiclo.data_fim_auto} onChange={handleChange} />
                    </div>
                    <div className="ad-form-group">
                        <label>Prazo Avaliação do Gestor</label>
                        <input type="date" name="data_fim_gestor" value={novoCiclo.data_fim_gestor} onChange={handleChange} />
                    </div>
                    
                    <hr style={{ gridColumn: '1 / -1', margin: '1rem 0' }} />
                    
                    <div className="ad-form-group">
                        <label>Modelo p/ Liderados (Obrigatório)</label>
                        <select name="modelo_id_liderado" value={novoCiclo.modelo_id_liderado} onChange={handleChange}>
                            <option value="">-- Selecione o modelo --</option>
                            {modelos.filter(m => m.status === 'ativo').map(m => (
                                <option key={m.id} value={m.id}>{m.titulo}</option>
                            ))}
                        </select>
                    </div>
                     <div className="ad-form-group">
                        <label>Modelo p/ Líderes (Opcional)</label>
                        <select name="modelo_id_lider" value={novoCiclo.modelo_id_lider} onChange={handleChange}>
                            <option value="">-- Usar o mesmo modelo dos liderados --</option>
                             {modelos.filter(m => m.status === 'ativo').map(m => (
                                <option key={m.id} value={m.id}>{m.titulo}</option>
                            ))}
                        </select>
                    </div>
                </form>
                 <div className="modal-footer" style={{ borderTop: 'none', marginTop: '2rem' }}>
                    <button className="ad-button-secondary" onClick={handleCloseModal}>Cancelar</button>
                    <button className="ad-button-primary" onClick={handleSubmit}>Criar Rascunho</button>
                </div>
            </Modal>
        </div>
    );
};


// =================================================================
// === COMPONENTE PRINCIPAL (HUB) ==================================
// =================================================================
const AvaliacaoDesempenho = ({ user }) => {
    // A tela de T&D já busca competências, mas se o usuário navegar direto para cá, precisamos delas
    const [competencias, setCompetencias] = useState([]);
    const [modelos, setModelos] = useState([]); // Modelos também são necessários nos ciclos
    const [loading, setLoading] = useState(true);
    
    // Verifica o perfil
    const { perfil } = useAuth();
    const isAdminRH = perfil === 'admin_geral' || perfil === 'admin' || perfil === 'rh';
    
    const [activeTab, setActiveTab] = useState(isAdminRH ? 'ciclos' : 'visaoGeral');

    // Carrega dados que são usados em múltiplas abas
    useEffect(() => {
        if (!isAdminRH) {
            setLoading(false);
            return; // Usuário comum não precisa carregar modelos ou competências aqui
        }

        const fetchAdminData = async () => {
            setLoading(true);
            try {
                const [compRes, modRes] = await Promise.all([
                    api.get('/api/rh/competencias'),
                    api.get('/api/rh/desempenho/modelos')
                ]);
                setCompetencias(compRes.data);
                setModelos(modRes.data);
            } catch (error) {
                toast.error("Falha ao carregar dados de suporte para avaliações.");
            } finally {
                setLoading(false);
            }
        };
        fetchAdminData();
    }, [isAdminRH]);

    const renderTabContent = () => {
        if (loading && isAdminRH) return <LoadingSpinner />;
        
        switch (activeTab) {
            case 'visaoGeral':
                return <VisaoGeralDashboard user={user} />;
            case 'modelos':
                return <GestaoModelos user={user} competencias={competencias} />;
            case 'ciclos':
                return <GestaoCiclos user={user} modelos={modelos} />;
            default:
                return null;
        }
    };

    return (
        <div className="ad-container">
            <div className="ad-header">
                <h1>Avaliação de Desempenho</h1>
            </div>

            {/* Navegação por Abas */}
            <div className="ad-tabs">
                <button 
                    className={`ad-tab-button ${activeTab === 'visaoGeral' ? 'active' : ''}`}
                    onClick={() => setActiveTab('visaoGeral')}
                >
                    <FaTasks /> Minhas Pendências
                </button>
                
                {/* Abas somente para Admin/RH */}
                {isAdminRH && (
                    <>
                        <button 
                            className={`ad-tab-button ${activeTab === 'ciclos' ? 'active' : ''}`}
                            onClick={() => setActiveTab('ciclos')}
                        >
                            <FaClipboardCheck /> Gestão de Ciclos
                        </button>
                        <button 
                            className={`ad-tab-button ${activeTab === 'modelos' ? 'active' : ''}`}
                            onClick={() => setActiveTab('modelos')}
                        >
                            <FaSitemap /> Modelos de Avaliação
                        </button>
                    </>
                )}
            </div>

            {/* Conteúdo da Aba */}
            <div className="ad-tab-content">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default AvaliacaoDesempenho;